import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import AllChannelsSelector, { selectionTargetsOtas } from '../common/AllChannelsSelector';
import { setRateRestrictionsRange } from '../../services/api';
import useChannelMappingRows from '../../hooks/useChannelMappingRows';
import { compareRatePlans } from '../../utils/ratePlanSort';
import RateActionSuccessDialog, { RATE_SUCCESS_COPY } from './RateActionSuccessDialog';
import {
  inventoryDrawerFooterSx,
  inventoryDrawerHeaderSx,
} from '../../utils/inventoryMobileLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';

const THEME_COLOR = '#4ce1b6';

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', shortLabel: 'S', color: '#FF6B6B' },
  { value: 1, label: 'Mon', shortLabel: 'M', color: '#4ECDC4' },
  { value: 2, label: 'Tue', shortLabel: 'T', color: '#45B7D1' },
  { value: 3, label: 'Wed', shortLabel: 'W', color: '#FFA07A' },
  { value: 4, label: 'Thu', shortLabel: 'T', color: '#98D8C8' },
  { value: 5, label: 'Fri', shortLabel: 'F', color: '#F7DC6F' },
  { value: 6, label: 'Sat', shortLabel: 'S', color: '#BB8FCE' },
];

const DEFAULT_FIELDS = {
  closeOnArrival: false,
  closeOnDeparture: false,
  minimumStay: '',
  maximumStay: '',
  minimumStayArrival: '',
  maximumStayArrival: '',
  exactStayArrival: '',
  minimumAdvanceReservation: '',
  maximumAdvanceReservation: '',
};

const rowKey = (roomTypeId, ratePlanId) => `${roomTypeId}:${ratePlanId}`;

/**
 * Group Restrictions drawer — rate-plan CTA/CTD/stay/advance via
 * Aiosell rate-restrictions-push (one API call). Stop Sell is separate.
 */
export default function GroupRateRestrictionsDrawer({
  open,
  onClose,
  roomTypes: _roomTypes = [],
  ratePlans: _ratePlans = [],
  channels = [],
  isDark,
  themeColors,
  onSaved,
}) {
  const [dateRange, setDateRange] = useState([null, null]);
  const [daySelectionType, setDaySelectionType] = useState('custom');
  const [selectedDays, setSelectedDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [selectedChannels, setSelectedChannels] = useState(['all']);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [action, setAction] = useState('apply'); // apply | clear
  const [fields, setFields] = useState({ ...DEFAULT_FIELDS });
  const [isSaving, setIsSaving] = useState(false);
  const { mappingPairs, mappingsLoading } = useChannelMappingRows(open);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successDialog, setSuccessDialog] = useState(null);
  const drawerRootRef = useRef(null);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? themeColors.surface : '#ffffff'),
    [isDark, themeColors.surface]
  );

  const planRows = useMemo(
    () =>
      mappingPairs.map((pair) => ({
        ...pair,
        key: rowKey(pair.roomTypeId, pair.ratePlanId),
      })),
    [mappingPairs]
  );

  const rowsByRoom = useMemo(() => {
    const map = new Map();
    planRows.forEach((row) => {
      if (!map.has(row.roomTypeId)) {
        map.set(row.roomTypeId, { name: row.roomTypeName, rows: [] });
      }
      map.get(row.roomTypeId).rows.push(row);
    });
    map.forEach((group) => {
      group.rows.sort(compareRatePlans);
    });
    return map;
  }, [planRows]);

  const resetForm = useCallback(() => {
    setDateRange([moment().add(1, 'day'), moment().add(2, 'day')]);
    setDaySelectionType('custom');
    setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
    setSelectedChannels(['all']);
    setAction('apply');
    setFields({ ...DEFAULT_FIELDS });
    setErrorMessage(null);
    setSuccessDialog(null);
    setSelectedKeys(new Set(planRows.map((r) => r.key)));
  }, [planRows]);

  useEffect(() => {
    if (open) resetForm();
    // Reset only when the drawer opens, not when mappings refresh after save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || planRows.length === 0) return;
    setSelectedKeys(new Set(planRows.map((row) => row.key)));
  }, [open, planRows]);

  const allSelected = planRows.length > 0 && selectedKeys.size === planRows.length;
  const someSelected = selectedKeys.size > 0 && selectedKeys.size < planRows.length;
  const clearMode = action === 'clear';

  const selectDayType = (type) => {
    setDaySelectionType(type);
    if (type === 'weekdays') setSelectedDays([1, 2, 3, 4, 5]);
    else if (type === 'weekend') setSelectedDays([0, 6]);
    else setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        const next = prev.filter((d) => d !== day);
        return next.length ? next : prev;
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const collectDates = () => {
    if (!dateRange[0] || !dateRange[1]) return [];
    const dates = [];
    let current = moment(dateRange[0]).startOf('day');
    const end = moment(dateRange[1]).startOf('day');
    while (current.isSameOrBefore(end, 'day')) {
      if (selectedDays.includes(current.day())) dates.push(current.format('YYYY-MM-DD'));
      current.add(1, 'day');
    }
    return dates;
  };

  const chipSx = (filled) => ({
    cursor: 'pointer',
    fontWeight: filled ? 700 : 500,
    backgroundColor: filled ? THEME_COLOR : 'transparent',
    color: filled ? '#000' : 'inherit',
    borderColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.45),
    borderWidth: filled ? 1.5 : 1,
    '&:hover': {
      backgroundColor: filled ? THEME_COLOR : alpha(THEME_COLOR, 0.08),
      borderColor: THEME_COLOR,
    },
  });

  const handleSave = async () => {
    setErrorMessage(null);
    setSuccessDialog(null);

    if (!dateRange[0] || !dateRange[1]) {
      setErrorMessage('Select a date range');
      return;
    }
    if (dateRange[0].isBefore(moment().startOf('day'), 'day')) {
      setErrorMessage('Cannot set restrictions for past dates');
      return;
    }
    if (selectedKeys.size === 0) {
      setErrorMessage('Select at least one rate plan');
      return;
    }
    if (!selectionTargetsOtas(selectedChannels)) {
      setErrorMessage('Turn on All Channels to push rate restrictions to OTAs');
      return;
    }
    if (!clearMode && !fields.closeOnArrival && !fields.closeOnDeparture) {
      const hasStay = [
        'minimumStay',
        'maximumStay',
        'minimumStayArrival',
        'maximumStayArrival',
        'exactStayArrival',
        'minimumAdvanceReservation',
        'maximumAdvanceReservation',
      ].some((k) => fields[k] !== '' && fields[k] != null);
      if (!hasStay) {
        setErrorMessage('Select CTA/CTD or enter at least one stay/advance restriction');
        return;
      }
    }

    const dates = collectDates();
    if (!dates.length) {
      setErrorMessage('No dates match the selected weekdays');
      return;
    }

    const items = planRows
      .filter((r) => selectedKeys.has(r.key))
      .map((r) => ({ room_type_id: r.roomTypeId, rate_plan_id: r.ratePlanId }));

    setIsSaving(true);
    try {
      const result = await setRateRestrictionsRange({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        dates,
        items,
        apply: !clearMode,
        restrictions: fields,
      });
      if (!result?.success) {
        setErrorMessage(result?.error || result?.message || 'Restrictions push failed');
        return;
      }
      if (typeof onSaved === 'function') await onSaved();
      setSuccessDialog(
        clearMode ? RATE_SUCCESS_COPY.restrictionsClear : RATE_SUCCESS_COPY.restrictionsApply
      );
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Restrictions push failed'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const stayFields = [
    { key: 'minimumStay', label: 'Min stay' },
    { key: 'maximumStay', label: 'Max stay' },
    { key: 'minimumStayArrival', label: 'Min stay arrival' },
    { key: 'maximumStayArrival', label: 'Max stay arrival' },
    { key: 'exactStayArrival', label: 'Exact stay arrival' },
    { key: 'minimumAdvanceReservation', label: 'Min advance' },
    { key: 'maximumAdvanceReservation', label: 'Max advance' },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !isSaving && onClose()}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 640 },
          overflow: 'hidden',
          backgroundColor: isDark ? themeColors.surface : '#ffffff',
        },
      }}
    >
      <Box
        ref={drawerRootRef}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...inventoryDrawerHeaderSx(isDark),
            position: 'relative',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, lineHeight: 1.2 }}>
              Restrictions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rate-plan CTA / CTD / stay · one push
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSaving} sx={{ color: themeColors.subtleText }}>
            <CloseIcon />
          </IconButton>
          {isSaving && (
            <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2 }} />
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                  Action
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {[
                    { value: 'apply', label: 'Apply' },
                    { value: 'clear', label: 'Clear all' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      onClick={() => setAction(opt.value)}
                      variant="outlined"
                      sx={chipSx(action === opt.value)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Date range
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <DatePicker
                  label="From"
                  value={dateRange[0]}
                  minDate={moment().startOf('day')}
                  onChange={(v) => {
                    if (!v) return;
                    const from = v.isBefore(moment().startOf('day'), 'day')
                      ? moment().startOf('day')
                      : v;
                    setDateRange([from, from.clone().add(1, 'day')]);
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
                <DatePicker
                  label="To"
                  value={dateRange[1]}
                  minDate={dateRange[0]?.clone().add(1, 'day')}
                  onChange={(v) => {
                    if (v && dateRange[0] && v.isAfter(dateRange[0])) {
                      setDateRange([dateRange[0], v]);
                    }
                  }}
                  slotProps={{
                    ...drawerOverlays.datePickerSlotProps,
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Day Selection
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pl: 1 }}>
                {[
                  { value: 'custom', label: 'Custom' },
                  { value: 'weekdays', label: 'Weekdays' },
                  { value: 'weekend', label: 'Weekend' },
                ].map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Radio
                        size="small"
                        checked={daySelectionType === opt.value}
                        onChange={() => selectDayType(opt.value)}
                        sx={{ color: alpha(THEME_COLOR, 0.6), '&.Mui-checked': { color: THEME_COLOR } }}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Box>
              {daySelectionType === 'custom' && (
                <Box sx={{ mt: 1.5, pl: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {DAY_OPTIONS.map((day) => {
                    const on = selectedDays.includes(day.value);
                    return (
                      <Chip
                        key={day.value}
                        label={`${day.shortLabel} ${day.label}`}
                        onClick={() => toggleDay(day.value)}
                        variant={on ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: on ? day.color : 'transparent',
                          color: on ? '#fff' : themeColors.subtleText,
                          borderColor: on ? day.color : themeColors.surfaceBorder,
                          fontWeight: on ? 600 : 400,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            {!clearMode && (
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 0.75,
                  }}
                >
                  Access
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.75 }}>
                  {[
                    { key: 'closeOnArrival', label: 'Close on Arrival', hint: 'CTA' },
                    { key: 'closeOnDeparture', label: 'Close on Departure', hint: 'CTD' },
                  ].map((item) => {
                    const on = !!fields[item.key];
                    return (
                      <Chip
                        key={item.key}
                        label={`${item.label} (${item.hint})`}
                        onClick={() =>
                          setFields((prev) => ({ ...prev, [item.key]: !prev[item.key] }))
                        }
                        variant="outlined"
                        sx={chipSx(on)}
                      />
                    );
                  })}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 0.75,
                  }}
                >
                  Stay & advance
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                  }}
                >
                  {stayFields.map((f) => (
                    <TextField
                      key={f.key}
                      size="small"
                      type="number"
                      label={f.label}
                      value={fields[f.key]}
                      onChange={(e) =>
                        setFields((prev) => ({ ...prev, [f.key]: e.target.value }))
                      }
                      inputProps={{ min: 0 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Rateplan matrix */}
            <Box
              sx={{
                mb: 2.5,
                borderRadius: 1.5,
                border: `1px solid ${themeColors.surfaceBorder}`,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr',
                  gap: 1,
                  px: 1.25,
                  py: 1,
                  alignItems: 'center',
                  backgroundColor: isDark ? alpha(THEME_COLOR, 0.12) : alpha(THEME_COLOR, 0.1),
                  borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() =>
                    setSelectedKeys(
                      allSelected ? new Set() : new Set(planRows.map((r) => r.key))
                    )
                  }
                  sx={{
                    p: 0.5,
                    color: alpha(THEME_COLOR, 0.6),
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: THEME_COLOR },
                  }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  RATEPLANS ({selectedKeys.size}/{planRows.length})
                </Typography>
              </Box>
              {Array.from(rowsByRoom.entries()).map(([roomId, group]) => (
                <Box key={roomId}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                      borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: THEME_COLOR, textTransform: 'uppercase' }}
                    >
                      {group.name}
                    </Typography>
                  </Box>
                  {group.rows.map((row) => {
                    const checked = selectedKeys.has(row.key);
                    return (
                      <Box
                        key={row.key}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr',
                          gap: 1,
                          px: 1.25,
                          py: 0.65,
                          alignItems: 'center',
                          borderBottom: `1px solid ${themeColors.surfaceBorder}`,
                          opacity: checked ? 1 : 0.55,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => {
                            setSelectedKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(row.key)) next.delete(row.key);
                              else next.add(row.key);
                              return next;
                            });
                          }}
                          sx={{
                            p: 0.5,
                            color: alpha(THEME_COLOR, 0.6),
                            '&.Mui-checked': { color: THEME_COLOR },
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                          {row.ratePlanName}
                          {row.mealPlan ? (
                            <Box component="span" sx={{ color: 'text.secondary' }}>
                              {' '}
                              ({row.mealPlan})
                            </Box>
                          ) : null}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {planRows.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {mappingsLoading
                      ? 'Loading mapped Channel Manager rate plans…'
                      : 'No mapped Channel Manager room types and rate plans. Save channel mappings first.'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 1 }}>
              <AllChannelsSelector
                channels={channels}
                value={selectedChannels}
                onChange={setSelectedChannels}
                allowPms={false}
                pmsMode="mirror"
                otaMode="display"
                themeColor={THEME_COLOR}
                disabled={isSaving}
                helperText="All Channels selects every OTA. Individual channels cannot be toggled."
              />
            </Box>
          </LocalizationProvider>
        </Box>

        <Box sx={{ p: 1.5, ...inventoryDrawerFooterSx(isDark), display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" fullWidth onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSave}
            disabled={isSaving || planRows.length === 0}
            sx={{
              backgroundColor: THEME_COLOR,
              color: '#000',
              fontWeight: 600,
              '&:hover': { backgroundColor: THEME_COLOR, opacity: 0.9 },
            }}
          >
            {isSaving ? 'Saving…' : clearMode ? 'Clear Restrictions' : 'Apply Restrictions'}
          </Button>
        </Box>
        <RateActionSuccessDialog
          open={Boolean(successDialog)}
          title={successDialog?.title}
          message={successDialog?.message}
          container={() => drawerRootRef.current || undefined}
          onClose={() => {
            setSuccessDialog(null);
            onClose();
          }}
        />
      </Box>
    </Drawer>
  );
}
